"use client";

// view components handle MFA and details
import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { FaBuilding, FaGlobe, FaPhoneAlt, FaVideo } from "react-icons/fa";
import Image from "next/image";
import { AuthSurface } from "@/components/auth";
import { MfaDialog } from "@/components/mfa";
import { Button, Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label, Tabs, TabsList, TabsTrigger } from "@/components/ui";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { formatPhoneNumber } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import type { PublicServiceDetail } from "@/types/service";
import { FlatServicesView } from "./components/flat-services-view";
import { FloatingServiceCounter } from "./components/floating-service-counter";
import { GroupedServicesView } from "./components/grouped-services-view";
import { MenuServicesView } from "./components/menu-services-view";
import { PreselectedServiceFlight, useArrivalPulse } from "./components/preselected-service-flight";
import { createSelectionHandler } from "./components/selection";
import { SelectionsDrawer } from "./components/selections-drawer";
import { ServiceFilters } from "./components/service-filters";
import { TaggedServicesView } from "./components/tagged-services-view";
import { useServicesView } from "./components/use-services-view";
import { ServiceDetailsModal } from "./service-details-modal";
import { useServices } from "./services-context";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { LocationType, SelfScheduleStep, useSelfScheduleStore } from "@/stores/self-schedule-store";
const Services = t0 => {
  const $ = _c(122);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...selfScheduleQueryOptions.frontPage()
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: frontPageData,
    isLoading
  } = useQuery(t1);
  const {
    setLocationType,
    locationType,
    shouldExposeLocationTypeChoice,
    selectedServices,
    selectedLocation,
    addSelectedService,
    userLocation,
    firstStep,
    urlPrefilledServices,
    clearUrlPrefilledServices
  } = useSelfScheduleContext();
  const {
    isPulsing: isCounterPulsing,
    pulse: pulseCounter
  } = useArrivalPulse();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    effectiveNumAllowedServices,
    shouldUseLargeTaggedLayout
  } = useServicesView(t2);
  const showServiceDuration = useServices().state.data?.showServiceDuration !== false;
  const {
    isSigningIn,
    setIsSigningIn,
    handlePatientVerifiedForService,
    handleExistingPatientServiceClick
  } = usePatientVerification();
  const [selectedServiceForDetails, setSelectedServiceForDetails] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [phoneOnlyService, setPhoneOnlyService] = useState(null);
  const [isPhoneOnlyModalOpen, setIsPhoneOnlyModalOpen] = useState(false);
  const [selectionsDrawerService, setSelectionsDrawerService] = useState(null);
  const {
    selectedAddons,
    selectedServiceInstances,
    setAddonsForService,
    removeAllInstancesOfService
  } = useSelfScheduleStore();
  const {
    isServiceSelected
  } = useSelfScheduleContext();
  let t3;
  if ($[2] !== frontPageData?.services) {
    t3 = serviceId => {
      const service = frontPageData?.services?.find(s => s.id === serviceId);
      if (!service) {
        return;
      }
      setSelectionsDrawerService(service);
    };
    $[2] = frontPageData?.services;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  frontPageData?.services;
  const handleOpenSelectionsDrawer = t3;
  let t4;
  if ($[4] !== addSelectedService || $[5] !== effectiveNumAllowedServices || $[6] !== isServiceSelected || $[7] !== selectedServiceInstances || $[8] !== selectionsDrawerService || $[9] !== setAddonsForService) {
    t4 = addonIds => {
      if (!selectionsDrawerService) {
        return;
      }
      const serviceId_0 = selectionsDrawerService.id;
      const alreadySelected = isServiceSelected(serviceId_0);
      const currentInstanceCount = selectedServiceInstances.filter(s_0 => s_0.id === serviceId_0).length;
      const shouldAppendInstance = !alreadySelected || selectionsDrawerService.allowMultipleBookings && selectedServiceInstances.length < effectiveNumAllowedServices;
      const occurrenceIndex = shouldAppendInstance ? currentInstanceCount + 1 : Math.max(currentInstanceCount, 1);
      if (shouldAppendInstance) {
        addSelectedService(serviceId_0, selectionsDrawerService.name);
      }
      setAddonsForService(`${serviceId_0}:${occurrenceIndex}`, addonIds);
      setSelectionsDrawerService(null);
    };
    $[4] = addSelectedService;
    $[5] = effectiveNumAllowedServices;
    $[6] = isServiceSelected;
    $[7] = selectedServiceInstances;
    $[8] = selectionsDrawerService;
    $[9] = setAddonsForService;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleSelectionsConfirm = t4;
  let t5;
  if ($[11] !== removeAllInstancesOfService || $[12] !== selectionsDrawerService) {
    t5 = () => {
      if (!selectionsDrawerService) {
        return;
      }
      removeAllInstancesOfService(selectionsDrawerService.id);
      setSelectionsDrawerService(null);
    };
    $[11] = removeAllInstancesOfService;
    $[12] = selectionsDrawerService;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleSelectionsRemove = t5;
  let t6;
  bb0: {
    if (!selectionsDrawerService) {
      t6 = undefined;
      break bb0;
    }
    const serviceId_1 = selectionsDrawerService.id;
    const currentInstanceCount_0 = selectedServiceInstances.filter(s_1 => s_1.id === serviceId_1).length;
    const isEditingLatestDuplicate = selectionsDrawerService.allowMultipleBookings && currentInstanceCount_0 > 0 && selectedServiceInstances.length >= effectiveNumAllowedServices;
    if (isEditingLatestDuplicate) {
      t6 = selectedAddons[`${serviceId_1}:${currentInstanceCount_0}`];
      break bb0;
    }
    if (selectionsDrawerService.allowMultipleBookings) {
      t6 = undefined;
      break bb0;
    }
    t6 = selectedAddons[`${serviceId_1}:1`] ?? selectedAddons[serviceId_1];
  }
  const existingDrawerAddonIds = t6;
  const hasNoServices = !frontPageData || (frontPageData.services?.length ?? 0) === 0;
  let t7;
  if ($[14] !== addSelectedService || $[15] !== handleExistingPatientServiceClick) {
    t7 = (serviceId_2, onSelect) => {
      handleExistingPatientServiceClick(serviceId_2, onSelect ?? addSelectedService);
    };
    $[14] = addSelectedService;
    $[15] = handleExistingPatientServiceClick;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const onRequireMfa = t7;
  let t8;
  if ($[17] !== frontPageData?.services) {
    t8 = serviceId_3 => {
      const service_0 = frontPageData?.services?.find(s_2 => s_2.id === serviceId_3);
      if (!service_0) {
        return;
      }
      setPhoneOnlyService(service_0);
      setIsPhoneOnlyModalOpen(true);
    };
    $[17] = frontPageData?.services;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  frontPageData?.services;
  const onPhoneOnlyService = t8;
  let t9;
  bb1: {
    let t10;
    if ($[19] !== frontPageData?.services) {
      t10 = serviceId_4 => {
        const service_1 = frontPageData?.services?.find(s_3 => s_3.id === serviceId_4);
        if (!service_1) {
          return;
        }
        setSelectedServiceForDetails(service_1);
        setIsDetailsModalOpen(true);
      };
      $[19] = frontPageData?.services;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    const onOpenDetails = t10;
    const shouldGroupByCategory = frontPageData?.shouldGroupServicesByCategory;
    const isMenuMode = frontPageData?.serviceDisplayMode === "MENU";
    if (shouldUseLargeTaggedLayout) {
      let t11;
      if ($[21] !== className || $[22] !== handleOpenSelectionsDrawer || $[23] !== onOpenDetails || $[24] !== onPhoneOnlyService || $[25] !== onRequireMfa) {
        t11 = <TaggedServicesView className={className} onOpenDetails={onOpenDetails} onRequireMfa={onRequireMfa} onPhoneOnlyService={onPhoneOnlyService} onOpenSelectionsDrawer={handleOpenSelectionsDrawer} />;
        $[21] = className;
        $[22] = handleOpenSelectionsDrawer;
        $[23] = onOpenDetails;
        $[24] = onPhoneOnlyService;
        $[25] = onRequireMfa;
        $[26] = t11;
      } else {
        t11 = $[26];
      }
      t9 = t11;
      break bb1;
    }
    if (isMenuMode) {
      let t11;
      if ($[27] !== className || $[28] !== handleOpenSelectionsDrawer || $[29] !== onOpenDetails || $[30] !== onPhoneOnlyService || $[31] !== onRequireMfa) {
        t11 = <MenuServicesView className={className} onOpenDetails={onOpenDetails} onRequireMfa={onRequireMfa} onPhoneOnlyService={onPhoneOnlyService} onOpenSelectionsDrawer={handleOpenSelectionsDrawer} />;
        $[27] = className;
        $[28] = handleOpenSelectionsDrawer;
        $[29] = onOpenDetails;
        $[30] = onPhoneOnlyService;
        $[31] = onRequireMfa;
        $[32] = t11;
      } else {
        t11 = $[32];
      }
      t9 = t11;
      break bb1;
    }
    if (userLocation || shouldGroupByCategory) {
      let t11;
      if ($[33] !== className || $[34] !== handleOpenSelectionsDrawer || $[35] !== onOpenDetails || $[36] !== onPhoneOnlyService || $[37] !== onRequireMfa) {
        t11 = <GroupedServicesView className={className} onOpenDetails={onOpenDetails} onRequireMfa={onRequireMfa} onPhoneOnlyService={onPhoneOnlyService} onOpenSelectionsDrawer={handleOpenSelectionsDrawer} />;
        $[33] = className;
        $[34] = handleOpenSelectionsDrawer;
        $[35] = onOpenDetails;
        $[36] = onPhoneOnlyService;
        $[37] = onRequireMfa;
        $[38] = t11;
      } else {
        t11 = $[38];
      }
      t9 = t11;
      break bb1;
    }
    let t11;
    if ($[39] !== className || $[40] !== handleOpenSelectionsDrawer || $[41] !== onOpenDetails || $[42] !== onPhoneOnlyService || $[43] !== onRequireMfa) {
      t11 = <FlatServicesView className={className} onOpenDetails={onOpenDetails} onRequireMfa={onRequireMfa} onPhoneOnlyService={onPhoneOnlyService} onOpenSelectionsDrawer={handleOpenSelectionsDrawer} />;
      $[39] = className;
      $[40] = handleOpenSelectionsDrawer;
      $[41] = onOpenDetails;
      $[42] = onPhoneOnlyService;
      $[43] = onRequireMfa;
      $[44] = t11;
    } else {
      t11 = $[44];
    }
    t9 = t11;
  }
  const ServicesView = t9;
  if (!frontPageData || isLoading) {
    return null;
  }
  if (hasNoServices) {
    let t10;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <h2 className="mb-4 text-h3 text-foreground md:text-2xl">No Services Available</h2>;
      $[45] = t10;
    } else {
      t10 = $[45];
    }
    let t11;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <span>We're currently not accepting online bookings for </span>;
      $[46] = t11;
    } else {
      t11 = $[46];
    }
    const t12 = locationType !== LocationType.ALL && locationType !== null ? `any ${locationType === LocationType.VIRTUAL ? "telehealth" : "in-person"} services at this location` : selectedLocation?.id ? "any services at this location" : "any services";
    let t13;
    if ($[47] !== t12) {
      t13 = <span>{t12}</span>;
      $[47] = t12;
      $[48] = t13;
    } else {
      t13 = $[48];
    }
    let t14;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <span>. Please call or text us directly to schedule your appointment.</span>;
      $[49] = t14;
    } else {
      t14 = $[49];
    }
    let t15;
    if ($[50] !== t13) {
      t15 = <div className="flex h-64 w-full flex-col items-center justify-center">{t10}<p className="max-w-md text-center text-body leading-relaxed text-foreground-secondary md:text-body-lg">{t11}{t13}{t14}</p></div>;
      $[50] = t13;
      $[51] = t15;
    } else {
      t15 = $[51];
    }
    return t15;
  }
  const t10 = firstStep === SelfScheduleStep.SERVICE ? "motion-opacity-in-0 motion-duration-1000" : "motion-opacity-in-0";
  let t11;
  if ($[52] !== className || $[53] !== t10) {
    t11 = cx("motion-ease-spring-bouncy", t10, className);
    $[52] = className;
    $[53] = t10;
    $[54] = t11;
  } else {
    t11 = $[54];
  }
  let t12;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <h1 className="text-h1 text-foreground">Choose a Service</h1>;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  let t13;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="mb-2">{t12}<div className="flex items-baseline justify-between"><p className="text-body text-muted-foreground">What would you like to do?</p></div></div>;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  let t14;
  if ($[57] !== shouldUseLargeTaggedLayout) {
    t14 = <ServiceFilters hideCategoryPicker={shouldUseLargeTaggedLayout} />;
    $[57] = shouldUseLargeTaggedLayout;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  let t15;
  if ($[59] !== locationType || $[60] !== setLocationType || $[61] !== shouldExposeLocationTypeChoice) {
    t15 = shouldExposeLocationTypeChoice && locationType === null && <div className="my-4"><Label>Choose a location type</Label><Tabs value={locationType ?? LocationType.ALL} onValueChange={value => setLocationType(value as LocationType)} className="w-fit"><TabsList className="grid grid-cols-3 gap-2 [&>*]:shrink-0"><TabsTrigger value={LocationType.ALL} className="flex items-center gap-2"><FaGlobe className="h-4 w-4 shrink-0" /><span>All</span></TabsTrigger><TabsTrigger value={LocationType.IN_PERSON} className="flex items-center gap-2"><FaBuilding className="h-4 w-4 shrink-0" /><span>In-Person</span></TabsTrigger><TabsTrigger value={LocationType.VIRTUAL} className="flex items-center gap-2"><FaVideo className="h-4 w-4 shrink-0" /><span>Telehealth</span></TabsTrigger></TabsList></Tabs></div>;
    $[59] = locationType;
    $[60] = setLocationType;
    $[61] = shouldExposeLocationTypeChoice;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  let t16;
  if ($[63] !== selectedServiceForDetails) {
    t16 = selectedServiceForDetails ? [selectedServiceForDetails] : undefined;
    $[63] = selectedServiceForDetails;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  let t17;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      setIsDetailsModalOpen(false);
      setSelectedServiceForDetails(null);
    };
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  let t18;
  if ($[66] !== addSelectedService || $[67] !== effectiveNumAllowedServices || $[68] !== frontPageData?.services || $[69] !== handleOpenSelectionsDrawer || $[70] !== isServiceSelected || $[71] !== locationType || $[72] !== onPhoneOnlyService || $[73] !== onRequireMfa || $[74] !== removeAllInstancesOfService || $[75] !== selectedServiceForDetails || $[76] !== selectedServiceInstances || $[77] !== selectedServices?.length) {
    t18 = () => {
      if (selectedServiceForDetails) {
        const hasCapacity = (selectedServices?.length || 0) < effectiveNumAllowedServices;
        const alreadySelected_0 = isServiceSelected(selectedServiceForDetails.id);
        const canAddDuplicate = selectedServiceForDetails.allowMultipleBookings;
        if (hasCapacity && (!alreadySelected_0 || canAddDuplicate)) {
          createSelectionHandler(frontPageData?.services, {
            addSelectedService,
            removeAllSelectedServiceInstances: removeAllInstancesOfService,
            selectedServiceIds: selectedServiceInstances.map(_temp),
            numAllowedServices: effectiveNumAllowedServices,
            onRequireMfa,
            onPhoneOnlyService,
            locationType,
            onOpenSelectionsDrawer: handleOpenSelectionsDrawer
          })(selectedServiceForDetails.id, "increment");
        }
      }
      setIsDetailsModalOpen(false);
      setSelectedServiceForDetails(null);
    };
    $[66] = addSelectedService;
    $[67] = effectiveNumAllowedServices;
    $[68] = frontPageData?.services;
    $[69] = handleOpenSelectionsDrawer;
    $[70] = isServiceSelected;
    $[71] = locationType;
    $[72] = onPhoneOnlyService;
    $[73] = onRequireMfa;
    $[74] = removeAllInstancesOfService;
    $[75] = selectedServiceForDetails;
    $[76] = selectedServiceInstances;
    $[77] = selectedServices?.length;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  let t19;
  if ($[79] !== isDetailsModalOpen || $[80] !== showServiceDuration || $[81] !== t16 || $[82] !== t18) {
    t19 = <ServiceDetailsModal services={t16} showDuration={showServiceDuration} isOpen={isDetailsModalOpen} onClose={t17} onSelect={t18} />;
    $[79] = isDetailsModalOpen;
    $[80] = showServiceDuration;
    $[81] = t16;
    $[82] = t18;
    $[83] = t19;
  } else {
    t19 = $[83];
  }
  const t20 = !!isSigningIn;
  let t21;
  if ($[84] !== addSelectedService || $[85] !== handlePatientVerifiedForService) {
    t21 = patient => handlePatientVerifiedForService(patient, addSelectedService);
    $[84] = addSelectedService;
    $[85] = handlePatientVerifiedForService;
    $[86] = t21;
  } else {
    t21 = $[86];
  }
  let t22;
  if ($[87] !== setIsSigningIn || $[88] !== t20 || $[89] !== t21) {
    t22 = <MfaDialog open={t20} onOpenChange={setIsSigningIn} surface={AuthSurface.SELF_SCHEDULE} onPatientVerified={t21} title="This service is only for returning patients" description="Enter your phone number to verify that you are a returning patient." tAndSFooterText="booking this service" />;
    $[87] = setIsSigningIn;
    $[88] = t20;
    $[89] = t21;
    $[90] = t22;
  } else {
    t22 = $[90];
  }
  let t23;
  if ($[91] !== frontPageData || $[92] !== phoneOnlyService) {
    t23 = phoneOnlyService && <DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle>Call or Text to Book!</DialogTitle><DialogDescription className="mt-1 text-body leading-6">{`${phoneOnlyService.name} can only be booked by calling or texting ${frontPageData?.tenantName} directly.`}</DialogDescription></DialogHeader><DialogBody><div className="flex items-center justify-center gap-4">{frontPageData.logoUrl && <Image src={frontPageData.logoUrl} alt={frontPageData.tenantName} className="rounded-full" width={50} height={50} />}{frontPageData?.phoneNumber && <div className="flex flex-col gap-1.5 py-4"><a href={`tel:${frontPageData.phoneNumber}`} className="flex items-center text-h2 text-primary hover:underline"><FaPhoneAlt className="mr-2 shrink-0" /><span>{formatPhoneNumber(frontPageData.phoneNumber)}</span></a><span className="text-body font-semibold text-muted-foreground">{frontPageData.tenantName}</span>{frontPageData?.slogan && <span className="text-body text-muted-foreground">{frontPageData.slogan}</span>}</div>}</div><p className="text-center text-body text-foreground-secondary">Let us know you're looking for "{phoneOnlyService.name}".</p></DialogBody><DialogFooter><DialogClose asChild={true}><Button variant="secondary" className="w-fit" onClick={() => {
            setIsPhoneOnlyModalOpen(false);
            setPhoneOnlyService(null);
          }}><span>Close</span></Button></DialogClose></DialogFooter></DialogContent>;
    $[91] = frontPageData;
    $[92] = phoneOnlyService;
    $[93] = t23;
  } else {
    t23 = $[93];
  }
  let t24;
  if ($[94] !== isPhoneOnlyModalOpen || $[95] !== t23) {
    t24 = <Dialog open={isPhoneOnlyModalOpen} onOpenChange={setIsPhoneOnlyModalOpen}>{t23}</Dialog>;
    $[94] = isPhoneOnlyModalOpen;
    $[95] = t23;
    $[96] = t24;
  } else {
    t24 = $[96];
  }
  let t25;
  if ($[97] !== ServicesView || $[98] !== t11 || $[99] !== t14 || $[100] !== t15 || $[101] !== t19 || $[102] !== t22 || $[103] !== t24) {
    t25 = <div data-testid="step-service" className={t11} key="services-container">{t13}{t14}{t15}{ServicesView}{t19}{t22}{t24}</div>;
    $[97] = ServicesView;
    $[98] = t11;
    $[99] = t14;
    $[100] = t15;
    $[101] = t19;
    $[102] = t22;
    $[103] = t24;
    $[104] = t25;
  } else {
    t25 = $[104];
  }
  let t26;
  if ($[105] !== existingDrawerAddonIds || $[106] !== handleSelectionsConfirm || $[107] !== handleSelectionsRemove || $[108] !== isServiceSelected || $[109] !== selectionsDrawerService) {
    t26 = selectionsDrawerService && <SelectionsDrawer service={selectionsDrawerService} existingAddonIds={existingDrawerAddonIds} isOpen={!!selectionsDrawerService} onOpenChange={() => setSelectionsDrawerService(null)} onConfirm={handleSelectionsConfirm} onRemove={isServiceSelected(selectionsDrawerService.id) ? handleSelectionsRemove : undefined} />;
    $[105] = existingDrawerAddonIds;
    $[106] = handleSelectionsConfirm;
    $[107] = handleSelectionsRemove;
    $[108] = isServiceSelected;
    $[109] = selectionsDrawerService;
    $[110] = t26;
  } else {
    t26 = $[110];
  }
  let t27;
  if ($[111] !== clearUrlPrefilledServices || $[112] !== pulseCounter || $[113] !== urlPrefilledServices) {
    t27 = <PreselectedServiceFlight services={urlPrefilledServices} onArrive={pulseCounter} onFinished={clearUrlPrefilledServices} />;
    $[111] = clearUrlPrefilledServices;
    $[112] = pulseCounter;
    $[113] = urlPrefilledServices;
    $[114] = t27;
  } else {
    t27 = $[114];
  }
  let t28;
  if ($[115] !== isCounterPulsing) {
    t28 = <FloatingServiceCounter isHighlighted={isCounterPulsing} />;
    $[115] = isCounterPulsing;
    $[116] = t28;
  } else {
    t28 = $[116];
  }
  let t29;
  if ($[117] !== t25 || $[118] !== t26 || $[119] !== t27 || $[120] !== t28) {
    t29 = <>{t25}{t26}{t27}{t28}</>;
    $[117] = t25;
    $[118] = t26;
    $[119] = t27;
    $[120] = t28;
    $[121] = t29;
  } else {
    t29 = $[121];
  }
  return t29;
};
export default Services;
function _temp(instance) {
  return instance.id;
}